import { useState, useEffect, SyntheticEvent } from "react";

import { MDBTable } from "mdbreact";
import { useDispatch, useSelector } from "react-redux";
import useAuditTrail from "../../custom-hooks/useAuditTrailData";
import { showModal } from "../../redux/actions/modal";
import { Loader } from "../../UI/Loaders/Loaders";
import Paginator from "../Pagination/Pagination.component";
import { appInsights } from "../../config/appInsights";
import { RootState } from "../../redux/store";
import CustomSelectDropdown from "../CustomHTMLElements/CustomSelectDropdown";
import { setAdvancedAuditTrailSearchParams } from "../../redux/actions/auditTrail";
import { getBlobData, getData } from "../../newApis/dotNetApiMethods";
import * as ajaxEndpoints from "../../api/ajax-endpoints";

const today = new Date().toISOString().slice(0, 10);
const lastMonth = new Date(new Date());
lastMonth.setDate(lastMonth.getDate() - 30);
const aMonthAgo = lastMonth.toISOString().slice(0, 10);

const sanitizeState = (state: Record<string, any>) => {
  const sanitizedState: Record<string, any> = {};
  for (const key in state) {
    if (state[key] !== undefined) {
      sanitizedState[key] =
        typeof state[key] === "number" ? state[key].toString() : state[key];
    }
  }
  return sanitizedState;
};

const AuditTrail = () => {
  const [state, setState] = useState({
    page: 1,
    pageSize: 20,
    start_date: aMonthAgo,
    end_date: today,
    count: 0,
    audit_type_id: "",
  });

  const [query, setQuery] = useState({
    page: 1,
    pageSize: 20,
    start_date: aMonthAgo,
    end_date: today,
    count: 0,
    audit_type_id: "",
  });
  const [isDownloading, setIsDownloading] = useState(false);

  const { searchParams } = useSelector(
    (state: RootState) => state?.auditTrailReducer
  );

  const dispatch = useDispatch();

  //  Methods
  const modalHandler = (modalData: any, type?: any) => {
    dispatch(
      showModal({
        modalIdentifier: "audittrail",
        dataRecord: modalData,
        action: "customrange",
        type,
      })
    );
  };

  const filterData = (e: SyntheticEvent) => {
    e.preventDefault();

    const diffTime =
      new Date(query.end_date).valueOf() - new Date(query.start_date).valueOf();

    if (diffTime < 0) {
      modalHandler("", "alert");
      return;
    }

    handleClear();
    setState({ ...query, page: 1 });
  };

  useEffect(() => {
    appInsights.trackPageView({
      name: "Audit/Trail - (AuditTrail.tsx)",
      isLoggedIn: true,
    });
  }, []);

  let RenderedComponent = null;

  const { data, status, error } = useAuditTrail({
    ...searchParams,
    // audit_type_id: state.audit_type_id,
    start_date: state.start_date,
    end_date: state.end_date,
    size: query.pageSize,
    page: query.page,
  });

  useEffect(() => {
    if (data) {
      setQuery((prev) => ({
        ...prev,
        count: data?.totalCount,
      }));
    }
  }, [data]);

  const handleDownloadDocument = async () => {
    setIsDownloading(true);
    try {
      const sanitizedState = sanitizeState(state);
      const response = await getBlobData(
        `${ajaxEndpoints.DOWNLOAD_AUDIT_REPORT}?${new URLSearchParams(sanitizedState)}`
      );

      const blob = new Blob([response.data], {
        type: "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `AuditTrailReport_${new Date().toISOString()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      dispatch(
        showModal({
          modalIdentifier: "feedback",
          dataRecord: {
            type: "failure",
            message: "Unable to download document. Contact admin",
          },
        })
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handleClear = () => {
    setQuery({
      page: 1,
      pageSize: 20,
      audit_type_id: "",
      count: data?.totalCount!,
      start_date: aMonthAgo,
      end_date: today,
    });
    setState((prev) => ({
      ...prev,
      page: 1,
      pageSize: 20,
      start_date: aMonthAgo,
      end_date: today,
      count: data?.totalCount!,
      audit_type_id: "",
    }));
    dispatch(setAdvancedAuditTrailSearchParams({}));
  };

  if (!data && status !== "loading") {
    RenderedComponent = (
      <div>
        <div className="grid__padding animated fadeInRight">
          <h4 className="center-txt">
            <i className="fas fa-bell" /> Unable to Fetch Audit trails
          </h4>
        </div>
      </div>
    );
  } else if (status === "loading") {
    RenderedComponent = (
      <Loader centered={true} text="Loading audit trail..." />
    );
  } else {
    if (error) {
      RenderedComponent = (
        <div>
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt p-y-5">
              <i className="fas fa-bell" /> There was an issue while fetching
              audit trails, Please Reload The Page{" "}
            </h4>
          </div>
        </div>
      );
    } else if (
      (data && data.data && data.data.length === 0) ||
      (data && !data.data)
    ) {
      RenderedComponent = (
        <div className="centerLoader">
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt p-y-5">
              <i className="fas fa-bell" /> There are currently no logged
              activities.
            </h4>
          </div>
        </div>
      );
    } else {
      RenderedComponent = (
        <div className="row m-x-sm m-y-2 grid__padding">
          <MDBTable responsive minimal="true" bordered hover>
            <thead className="bg-lighter-grey">
              <tr>
                <th>S/N</th>
                <th>Username</th>
                <th>Audit Type</th>
                <th>Client IP Address</th>
                <th>Date</th>
                <th>Time</th>
                <th>Manage</th>
              </tr>
            </thead>
            <tbody>
              {data &&
                data.data &&
                data?.data.map((auditTrail, _id) => {
                  const {
                    audit_type,
                    client_ip_address,
                    action_date,
                    username,
                    id,
                  } = auditTrail;
                  return (
                    <tr key={id}>
                      <td>{(query.page - 1) * query.pageSize + (_id + 1)}</td>
                      <td>{username}</td>
                      <td>{audit_type}</td>
                      <td>{client_ip_address}</td>
                      <td>{action_date.substring(0, 10)}</td>
                      <td>{action_date.substring(11, 19)}</td>
                      <td>
                        <span
                          id="0"
                          data-table="Notifications"
                          data-action="view"
                          className="Notifications view btn-manage zoom-element btn-slim"
                          onClick={() => {
                            modalHandler(auditTrail);
                          }}
                        >
                          View
                        </span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </MDBTable>
        </div>
      );
    }
  }

  return (
    <div className="datatable-wrapper">
      <div className="table-view speed-1x fadeInRight">
        <h3 className="page-title mb-4">Audit Trail</h3>

        <div className="row d-flex justify-content-end">
          <button
            className="btn advancly-btn mr-4"
            onClick={handleDownloadDocument}
            disabled={isDownloading} // Disable button while downloading
          >
            {isDownloading ? (
              <>
                <i className="fas fa-spinner fa-spin" />
                &nbsp; Downloading...
              </>
            ) : (
              <>
                <i className="fas fa-download" />
                &nbsp; Download
              </>
            )}
          </button>
        </div>

        <div className="row">
          <div className="form-group col-md-3 col-xl-2">
            <label>From</label>
            <div className="input-group">
              <input
                type="date"
                name="start_date"
                className="form-control"
                value={query.start_date}
                onChange={(e) => {
                  setQuery((prev) => ({
                    ...prev,
                    start_date: e.target.value,
                  }));
                }}
              />
            </div>
          </div>
          <div className="form-group col-md-3 col-xl-2">
            <label>To</label>
            <div className="input-group">
              <input
                type="date"
                className="form-control"
                name="end_date"
                value={query.end_date}
                onChange={(e) => {
                  setQuery((prev) => ({
                    ...prev,
                    end_date: e.target.value,
                  }));
                }}
              />
            </div>
          </div>
          <div
            className="form-group col-md-3 col-xl-2"
            style={{ marginTop: "30px" }}
          >
            <div className="input-group">
              <CustomSelectDropdown
                name="size"
                value={query.pageSize}
                onChange={(e) => {
                  handleClear();
                  setQuery((prev) => ({
                    ...prev,
                    pageSize: Number(e.target.value),
                  }));
                }}
                isShowDropdownArrow
              >
                <option value={10}>10 Entries</option>
                <option value={20}>20 Entries</option>
                <option value={50}>50 Entries</option>
                <option value={100}>100 Entries</option>
              </CustomSelectDropdown>
            </div>
          </div>
          <div
            className="form-group col-md-3 col-xl-2"
            style={{ marginTop: "30px" }}
          >
            <div className="input-group">
              <button
                className="btn advancly-white-btn ml-2 form-control"
                onClick={(e) => {
                  e.preventDefault();
                  setQuery((prev) => ({
                    ...prev,
                    page: 1,
                  }));
                  dispatch(
                    showModal({
                      modalIdentifier: "advancedaudittrailsearch",
                    })
                  );
                }}
              >
                <i className="fas fa-search" />
                &nbsp; Advance Search
              </button>
            </div>
          </div>
          <div
            className="form-group col-md-3 col-xl-2"
            style={{ marginTop: "30px" }}
          >
            <div className="input-group">
              <button
                className="btn advancly-btn ml-2 form-control"
                onClick={filterData}
              >
                Search
              </button>
            </div>
          </div>
          <div
            className="form-group col-md-3 col-xl-2"
            style={{ marginTop: "30px" }}
          >
            <div className="input-group">
              <button
                type="submit"
                className={`btn advancly-red-btn btn-md ml-3`}
                style={{ height: "3rem" }}
                onClick={handleClear}
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/*Datatable || Loader || Error Message*/}
        {RenderedComponent}

        <Paginator
          size={query.pageSize}
          page={query.page}
          count={query.count}
          changeCurrentPage={(pageNumber: number) => {
            setQuery((prev) => ({ ...prev, page: pageNumber }));
          }}
        />
      </div>
    </div>
  );
};

export default AuditTrail;
